import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import cx from 'classnames';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Toast from '@/components/Toast';
import Tips from '@/components/Tips/mIndex';
import CommonInput from '@/components/CommonInput/mIndex';
import CommonTextarea from '@/components/CommonTextarea/mIndex';
import CommonPageModal from '@/components/CommonPageModal/mIndex';
import LinkModal from '@/components/LinkModal/mIndex';
import DeviceModal from '@/components/DeviceModal/mIndex';
import LinkPreview from '@/components/LinkPreview/mIndex';
import PhotoPreview from '@/components/PhotoPreview/mIndex';
import VideoPreview from '@/components/VideoPreview/mIndex';
import {
  IS_ANDROID,
  IS_DJI_FLY,
  MAX_RELATED_DEVICES,
  MAX_TITLE_LENGTH,
  MAX_DESC_LENGTH,
} from '@/constants';
import {
  IMAGE_ACCEPT_FORMAT,
  VIDEO_ACCEPT_FORMAT,
  MAX_PHOTOS,
  MAX_VIDEOS,
} from '@/constants/upload';
import useValidEditArtical, { calcTextLength } from '@/hooks/useValidEditArtical';
import { IDevice } from '@/api/device';
import { ContentTypeEnum } from '@/api/thread';
import {
  UploadTypeEnum, AliOSSUploadConfig,
  initVideoUploadService, uploadToOSSService, uploadPhotoService, uploadVideoToBackend,
} from '@/api/upload';
import {
  ActionTypeEnum,
  IProcessFile,
  IEditorContentItem,
  IEditorAction,
  IEditor,
  preCheckFiles,
  getVideoCoverMobile,
  ErrorMsg,
  customQueryTitleMap,
} from './config';
import { getRandomKey, addItemsToArray } from '@/utils';
import ls from '@/utils/localStorage';
import { ErrorCodes } from '@/utils/fetch';
import Styles from './mIndex.module.scss';
import { SafeAreaCtx } from '@/utils/pageEntry';
import useSls from '@/hooks/useSls';
import { reportUploadMetric } from './utils/reportMetrics';

interface ICheckModal {
  visible?: boolean;
  key?: string;
  type?: ContentTypeEnum;
}

const ModuleEditor = ({
  className = '',
  subjects = [],
  recommendDevices = [],
  deviceCategories = [],
  value,
  onChange,
  canPost,
  isEdit,
  onUploadResult,
}: IEditor) => {
  const router = useRouter();
  // 上报sls埋点
  const { reportSlsData } = useSls();

  const { disabledTheme, queryTheme, queryTitle } = useMemo(() => {
    const { theme, titleid } = router.query;
    return isEdit ? {} : {
      disabledTheme: !!theme,
      queryTheme: theme,
      queryTitle: customQueryTitleMap?.[titleid as keyof typeof customQueryTitleMap] || '',
    }
  }, [isEdit, router]);

  // 编辑器内容
  const [data, setData] = useState<IEditorContentItem[]>(
    (value.items || []).map(({ key, nameindex, ...rest }) => ({ ...rest, key: key || nameindex || '', percent: 1 }))
    || []
  );

  const dataRef = useRef<IEditorContentItem[]>((value.items || []).map(({ key, nameindex, ...rest }) => ({ ...rest, key: key || nameindex || '', percent: 1 })) || []);

  // 关联内容
  const [relatedContent, setRelatedContent] = useState<any>({
    title: queryTitle || value.title,
    subject: queryTheme || value.type_id || subjects[0]?.type_id,
    devices: value.devices || [],
  });
  const [uploadType, setUploadType] = useState<ActionTypeEnum>();
  const [currentFocusText, setCurrentFocusText] = useState((
    (value.items || [])[0]?.key)
    || ((value.items || [])[0]?.nameindex)
    || 'first-text'
  );
  const [linkModalVisbile, setLinkModalVisible] = useState(false);
  const [upgradeFlyAppModalVisible, setUpgradeFlyAppModalVisible] = useState(false);
  const [deviceModalVisible, setDeviceModalVisible] = useState(false);
  const [checkModal, setCheckModal] = useState<ICheckModal>({});
  const [showTips, setShowTips] = useState(false);
  const t = useTranslations();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { validFileMaxCount } = useValidEditArtical();

  const IS_ANDROID_FLY = IS_ANDROID && IS_DJI_FLY;

  useEffect(() => {
    setShowTips(!ls.get('m-edit-tips')?.done);
  }, [ls.get('m-edit-tips')?.done]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (relatedContent.devices.length > 0) {
      const lastDeviceKey = relatedContent.devices[relatedContent.devices.length - 1].key;
      document.getElementById(lastDeviceKey)?.scrollIntoView(true);
    }
  }, [relatedContent.devices]);

  useEffect(() => {
    const val = {
      message: dataRef.current.map((elem) => {
        if (elem.type !== ContentTypeEnum.video) {
          return elem;
        }
        // 字段裁剪
        const { ossConfig: _, ...rest } = elem;

        return rest;
      }),
      ...relatedContent,
    };
    onChange(val);
  }, [dataRef.current, relatedContent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelRequest = (requestKey: string) => {
    const currentCancelItem = dataRef.current.filter(dataRefItem => dataRefItem.key === requestKey)[0];

    if (typeof currentCancelItem?.abort === 'function' && uploadType === ActionTypeEnum.video && currentCancelItem?.checkpoint) {
      currentCancelItem.abort(currentCancelItem.checkpoint);
    } else if (typeof currentCancelItem?.controller?.abort === 'function' && uploadType === ActionTypeEnum.photo) {
      currentCancelItem?.controller?.abort();
    }
  };

  const handleOpenCheckModal = (contentKey: string, type: ContentTypeEnum) => {
    setCheckModal({
      visible: true,
      key: contentKey,
      type,
    });
  };

  const handleCloseCheckModal = () => {
    setCheckModal({});
  };

  const handleDeleteContent = () => {
    if (checkModal.type === ContentTypeEnum.photo || checkModal.type === ContentTypeEnum.video) {
      handleCancelRequest(checkModal.key as string);
    }

    const targetIndex = dataRef.current.findIndex(({ key }) => key === checkModal.key);
    const prevItem = dataRef.current[targetIndex - 1];
    const nextItem = dataRef.current[targetIndex + 1];
    const cond = nextItem && nextItem.type == ContentTypeEnum.text && !nextItem.desc && prevItem.type == ContentTypeEnum.text;
    const newData = [...dataRef.current];
    newData.splice(targetIndex, cond ? 2 : 1);

    setData(newData);
    dataRef.current = newData;
    setCheckModal({});
    setCurrentFocusText('');
  };

  /** 文本 */
  const handleAddText = (contentId: string, val?: string) => {
    const newData = dataRef.current.map((elem) => {
      const { key, ...rest } = elem;
      if (key !== contentId) {
        return elem;
      }
      return {
        ...rest,
        key,
        desc: val,
      };
    });

    setData(newData);
    dataRef.current = newData;
  };

  const handleAddEmptyText = (currentId: string, currentValue: string) => {
    const targetIndex = dataRef.current.findIndex(({ key }) => key === currentId);
    const newTextKey = getRandomKey();

    // 保存当前值，并创建新空 textrea 节点
    const newData = addItemsToArray(dataRef.current, targetIndex, 1, [
      { key: currentId, type: ContentTypeEnum.text, desc: currentValue },
      { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
    ]);

    setData(newData);
    dataRef.current = newData;
    setCurrentFocusText(newTextKey)
  };

  const handleCombineText = (currentId: string, currentValue: string) => {
    if (dataRef.current.length <= 1 || currentId === dataRef.current[0].key) {
      return;
    }

    const targetIndex = dataRef.current.findIndex(({ key }) => key === currentId);
    const isPrevText = dataRef.current[targetIndex - 1].type === ContentTypeEnum.text;

    if (!isPrevText) {
      return;
    }

    setCurrentFocusText(dataRef.current[targetIndex - 1].key);
    const newData = dataRef.current
      .map((elem, index) => {
        if (index === targetIndex - 1) {
          return {
            ...elem,
            desc: `${elem.desc}${currentValue}`,
          };
        }
        return elem;
      })
      .filter(({ key }: { key: string}) => key !== currentId);

    setData(newData);
    dataRef.current = newData;
  };

  const handleDeleteEmptyText = (currentId: string) => {
    // 只有一个文本元素不做处理
    if (dataRef.current.length <= 1 || currentId === dataRef.current[0].key) {
      return;
    }

    const targetIndex = dataRef.current.findIndex(({ key }) => key === currentId);
    const isPrevText = dataRef.current[targetIndex - 1].type === ContentTypeEnum.text;

    /**
     * 目标节点的上个节点是 text 的话，就移动 focus 到上一个
     * 目标节点的上个节点是 media 的话，就停止删除，保持最后一个 text，不改变光标
     */
    setCurrentFocusText(isPrevText ? dataRef.current[targetIndex - 1].key : dataRef.current[targetIndex].key);
    const newData =  isPrevText
      ? dataRef.current.filter(({ key }) => key !== currentId)
      : dataRef.current.map((elem) => elem.key === currentId ? ({ ...elem, desc: '' }) : elem)

    setData(newData);
    dataRef.current = newData;
  };

  const handleChangeFocusText = (textId: string) => {
    setCurrentFocusText(textId);
  };

  /** 图片 */
  const handleAddPhoto = (file: File, url: string, newPhotoKey: string, controller: AbortController, width?: number, height?: number) => {
    const targetIndex = dataRef.current.findIndex(({ key }) => key === currentFocusText);
    const newTextKey = getRandomKey();
    const ratio = !!width && !!height ? `${height / width}` : undefined;

    const newData = (targetIndex === dataRef.current.length - 1 || !currentFocusText)
      ? [
        // 添加在最下方
        ...dataRef.current,
        { key: newPhotoKey, type: ContentTypeEnum.photo, desc: ratio, url, controller, file },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]
      : addItemsToArray(dataRef.current, targetIndex, 1, [
        { key: currentFocusText, type: ContentTypeEnum.text, desc: dataRef.current[targetIndex]?.desc },
        { key: newPhotoKey, type: ContentTypeEnum.photo, desc: ratio, url, controller, file },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]);

    setCurrentFocusText(newTextKey);
    setData(newData);
    dataRef.current = newData;
    validFileMaxCount({
      type: ActionTypeEnum.photo,
      fileMaxCount: MAX_PHOTOS + 1,
      langKey: 'error_multiple_photo_maximum',
      data: newData,
    });
  };

  /** 视频 */
  const handleAddVideo = async (file: File, duration: number, config: AliOSSUploadConfig, newVideoKey: string) => {
    const targetIndex = data.findIndex(({ key }) => key === currentFocusText);
    const newTextKey = getRandomKey();

    const newData = targetIndex === dataRef.current.length - 1
      ? [
        // 添加在最下方
        ...dataRef.current,
        { key: newVideoKey, type: ContentTypeEnum.video, url: config.vid, duration, ossConfig: config, file },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]
      : addItemsToArray(dataRef.current, targetIndex, 1, [
        { key: currentFocusText, type: ContentTypeEnum.text, desc: data[targetIndex]?.desc },
        { key: newVideoKey, type: ContentTypeEnum.video, url: config.vid, duration, ossConfig: config, file },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]);

    setCurrentFocusText(newTextKey);
    setData(newData);
    dataRef.current = newData;
    validFileMaxCount({
      type: ActionTypeEnum.video,
      fileMaxCount: MAX_VIDEOS + 1,
      langKey: 'error_multiple_video_maximum',
      data: newData,
    });
    return newVideoKey;
  };

  /** 链接 */
  const handleOpenLinkModal = () => {
    setLinkModalVisible(true);
  };

  const handleCancelLinkModal = () => {
    setLinkModalVisible(false);
  };

  const handleAddLink = (textId: string, linkUrl: string, linkTitle: string) => {
    const targetIndex = dataRef.current.findIndex(({ key }) => key === textId);
    const newTextKey = getRandomKey();

    const newData = targetIndex === dataRef.current.length - 1
      ? [
        // 添加在最下方
        ...data,
        { key: getRandomKey(), type: ContentTypeEnum.iframeLink, url: linkUrl, desc: linkTitle },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]
      : addItemsToArray(dataRef.current, targetIndex, 1, [
        { key: textId, type: ContentTypeEnum.text, desc: data[targetIndex]?.desc },
        { key: getRandomKey(), type: ContentTypeEnum.iframeLink, url: linkUrl, desc: linkTitle },
        { key: newTextKey, type: ContentTypeEnum.text, desc: '' },
      ]);


    setLinkModalVisible(false);
    setCurrentFocusText(newTextKey);
    setData(newData);
    dataRef.current = newData;
  };

  /** 主题 */
  const handleSelectSubject = (typeId: number) => {
    if (disabledTheme) {
      return;
    }
    setRelatedContent({ ...relatedContent, subject: typeId })
  };

  /** 设备 */
  const handleOpenDeviceModal = () => {
    setDeviceModalVisible(true);
  };

  const handleCancelDeviceModal = () => {
    setDeviceModalVisible(false);
  };

  const handleAddDevice = (selectedDevices: IDevice[]) => {
    setRelatedContent({
      ...relatedContent,
      devices: selectedDevices,
    });
    setDeviceModalVisible(false);
  };

  const handleDeleteDevice = (deviceKey: string) => {
    setRelatedContent({
      ...relatedContent,
      devices: relatedContent.devices.filter(({ key }: { key: string }) => key !== deviceKey),
    })
  };

  const isDeviceExist = (deviceKey: string) => relatedContent.devices.filter(({ key }: { key: string }) => key === deviceKey).length > 0;

  const actions = useMemo((): IEditorAction[] => {
    return [
      {
        slug: ActionTypeEnum.photo,
        label: t('btn_photo'),
        isUploadFile: true,
        multiple: true,
        accept: IMAGE_ACCEPT_FORMAT,
        callback: (e) => {
          if(!validFileMaxCount({
            type: ActionTypeEnum.photo,
            langKey: "error_photo_maximum",
            data: dataRef.current,
          })) {
            e?.preventDefault();
            return;
          }
          setUploadType(ActionTypeEnum.photo);
        },
      },
      {
        slug: ActionTypeEnum.video,
        label: t('btn_video'),
        isUploadFile: true,
        multiple: true,
        accept: VIDEO_ACCEPT_FORMAT,
        callback: (e) => { 
          if(!validFileMaxCount({
            type: ActionTypeEnum.video,
            langKey: "error_video_maximum",
            data: dataRef.current,
          })) {
            e?.preventDefault();
            return;
          }
          setUploadType(ActionTypeEnum.video);
        },
      },
      {
        slug: ActionTypeEnum.link,
        label: t('btn_link'),
        isUploadFile: false,
        callback: handleOpenLinkModal
      },
    ];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadToOSS = (configs: AliOSSUploadConfig, fileItem: IProcessFile, controller: AbortController, videoKey: string) => {
    const { file } = fileItem;
    const curDataRef = dataRef.current.filter((dataItem)=> dataItem.key === videoKey)

    const successCb = () => {
      uploadVideoToBackend({ configs, controller, file: file });
    };

    const failCb = (e: any) => {};

    const { task, abort } = uploadToOSSService({
      ...configs,
      upload_file_path: configs.object_id,
    }, file as File, {
      checkpoint: curDataRef[0].checkpoint,
      progress: (p: number, cpt: any) => {
        const newData = dataRef.current.map((dataItem) => {
          if (dataItem.key === videoKey) {
            dataItem.percent = p;
            dataItem.isCompleted = p === 1;
            dataItem.error = null;
            dataItem.checkpoint = cpt;
            dataItem.abort = abort;
          }
          return dataItem;
        })
        setData(newData);
        dataRef.current = newData;
      },
      parallel: 4,
      partSize: 1000 * 1024,
    });

    task.then(() => {
      successCb();
    }).catch((e: any) => {
      handleUploadError(videoKey, e);
      failCb(e);
    });
  };

  const handleUploadError = (activeKey: string, e: any) => {
    const newData = dataRef.current.map((dataItem)=>{
      if(dataItem.key === activeKey){
        dataItem.isCompleted = false;
        // 保留完整的错误对象，以便PhotoPreview能够访问code和message
        const errorData = e?.response?.data?.message || e?.message || e || t('error_upload_retry');
        dataItem.error = errorData;
      }
      return dataItem;
    })
    setData(newData);
    dataRef.current = newData;
    if(e?.code === ErrorCodes.requestLimit) {
      Toast.show({
        content: e?.message || t('tips_request_max_limit'),
      });
    }
  };

  // 文件选择回调
  const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const { files } = e.target;
    if (!files) {
      return;
    }

    const { acceptFiles, rejectFiles } = await preCheckFiles(Array.from(files), type);

    if (rejectFiles.length > 0) {
      const first = rejectFiles[0];
      const errorInfo = ErrorMsg(type as ActionTypeEnum, first?.error);
      Toast.show({
        content: t(errorInfo?.id, errorInfo?.value || {}),
      });
    }

    acceptFiles.forEach((fileItem: IProcessFile) => {
      const controller = new AbortController();
      const randomKey = getRandomKey();
      const { file, width, height, duration, url } = fileItem;

      if (type === ActionTypeEnum.photo) {
        handleAddPhoto(file, url, randomKey,  controller, width, height);
        // 记录上传开始时间
        const startTime = Date.now();
        // 图片上传开始埋点
        reportUploadMetric({
          file,
          type: 'image',
          status: 'click',
          startTime,
        }, reportSlsData);

        uploadPhotoService({
          file,
          controller,
          onUploadProgressCb: (percent: number) => {
            const newData = dataRef.current.map((dataItem) => {
              if (dataItem.key === randomKey) {
                dataItem.percent = percent;
                dataItem.error = null;
              }
              return dataItem;
            });
            setData(newData);
            dataRef.current = newData;
          },
        }).then((url: string) => {
          const newData = dataRef.current.map((dataItem) => {
            if (dataItem.key === randomKey) {
              dataItem.isCompleted = true;
              dataItem.url = url;
            }
            return dataItem;
          });
          setData(newData);
          dataRef.current = newData;

          // 图片上传成功埋点
          reportUploadMetric({
            file,
            type: 'image',
            status: 'success',
            startTime,
          }, reportSlsData);

          onUploadResult?.({
            type: 'image',
            is_success: true,
            upload_duration: Date.now() - startTime,
          });
        }).catch((e)=> {
          handleUploadError(randomKey, e);
          if(e?.message) {
            Toast.show({
              content: e.message,
            });
          }

          // 图片上传失败埋点
          reportUploadMetric({
            file,
            type: 'image',
            status: 'fail',
            startTime,
            error: e,
          }, reportSlsData);

          onUploadResult?.({
            type: 'image',
            is_success: false,
            upload_duration: Date.now() - startTime,
            fail_reason: e?.message || e?.code,
          });
        });
      } else if(type === ActionTypeEnum.video) {
        if (IS_ANDROID_FLY) {
          setUpgradeFlyAppModalVisible(true);
        }
        // 视频上传开始埋点
        const startTime = Date.now();
        reportUploadMetric({
          file,
          type: 'video',
          status: 'click',
          startTime,
          additionalParams: {
            video_duration: duration || 0
          }
        }, reportSlsData);

        initVideoUploadService({
          workType: UploadTypeEnum.video,
          controller,
        }).then((res: AliOSSUploadConfig) => {
          handleAddVideo(file, duration as number, res, randomKey);
          handleUploadToOSS(res, fileItem, controller, randomKey);

          // 视频上传成功埋点
          reportUploadMetric({
            file,
            type: 'video',
            status: 'success',
            startTime,
            additionalParams: {
              video_duration: duration || 0
            }
          }, reportSlsData);

          onUploadResult?.({
            type: 'video',
            is_success: true,
            upload_duration: Date.now() - startTime,
          });

          getVideoCoverMobile(file).then(({ dataURL, coverFile }: any) => {
            const coverController = new AbortController();

            // 记录上传开始时间
            const coverStartTime = Date.now();
            
            // 封面图片上传开始埋点
            reportUploadMetric({
              file: coverFile,
              type: 'image',
              status: 'click',
              startTime: coverStartTime,
            }, reportSlsData);

            uploadPhotoService({
              file: coverFile,
              controller: coverController,
            }).then((url: string) => {
              const dataRefTemp = dataRef.current.map((dataItem)=>{
                if (dataItem.key === randomKey) {
                  dataItem.imgsrc = (url || dataURL) as string;
                }
                return dataItem;
              });
              setData(dataRefTemp);
              dataRef.current = dataRefTemp;

              // 封面图片上传成功埋点
              reportUploadMetric({
                file: coverFile,
                type: 'image',
                status: 'success',
                startTime: coverStartTime,
              }, reportSlsData);

            }).catch((e)=> {
              // 封面图片上传失败埋点
              reportUploadMetric({
                file: coverFile,
                type: 'image',
                status: 'fail',
                startTime: coverStartTime,
                error: e,
              }, reportSlsData);

              handleUploadError(randomKey, e);
            });
          });
        }).catch((e) => {
          handleUploadError(randomKey, e);
          if(e?.message) {
            Toast.show({
              content: e.message,
            });
          }

          // 视频上传失败埋点
          reportUploadMetric({
            file,
            type: 'video',
            status: 'fail',
            startTime,
            error: e,
            additionalParams: {
              video_duration: duration || 0
            }
          }, reportSlsData);

          onUploadResult?.({
            type: 'video',
            is_success: false,
            upload_duration: Date.now() - startTime,
            fail_reason: e?.message || e?.code,
          });

        });
      }
    });

    // 避免两次上传相同文件无效
    e.target.value = '';
    return;
  };

  const onRetryUpload = (id: string) => {
    const retryItem = dataRef.current.filter(({ key })=> id === key);
    if (retryItem[0]?.type === ContentTypeEnum.photo) {
      const { file, controller } = retryItem[0];
      uploadPhotoService({
        file: file as File,
        controller,
        onUploadProgressCb: (p: number) => {
          const newData = dataRef.current.map((dataItem)=>{
            if (dataItem.key === id) {
              dataItem.percent = p;

              dataItem.error = null;
            }
            return dataItem;
          });

          setData(newData);
          dataRef.current = newData;
        },
      }).then((url: string) => {
        const newData = dataRef.current.map((dataItem)=>{
          if (dataItem.key === id) {
            dataItem.isCompleted = true;
            dataItem.url = url;
          }
          return dataItem;
        });
        setData(newData);
        dataRef.current = newData;
      }).catch((e)=> {
        handleUploadError(id, e);
      });
    } else if (retryItem[0]?.type === ContentTypeEnum.video) {
      const {
        controller=new AbortController(), ossConfig = {},
      } = retryItem[0];
      handleUploadToOSS(ossConfig as AliOSSUploadConfig, retryItem[0] as IProcessFile, controller, id);
    }
  };

  const renderTopCreditTips = () => {
    const handleNavigate = () => {
      router.push('https://bbs.dji.com/forum.php?mod=viewthread&tid=372564');
    };

    return (
      <div className={Styles.topCreditTips}>
        <div className={Styles.tips}>{t('tips_credits_error')}</div>
        <div className={Styles.nav} onClick={handleNavigate}>{t('tips_nav_credits')}</div>
      </div>
    );
  };

  const { safeAreaHeight } = useContext(SafeAreaCtx);

  return (
    <div className={cx(Styles.root, className)}>
      <div
        className={cx(Styles.scrollArea, {
          [Styles.taller]: relatedContent.devices.length > 0,
        })}
        ref={scrollRef}
      >
        { !canPost && renderTopCreditTips() }
        {/** 主题分类区 */}
        <div className={Styles.subjects}>
          {
            showTips && <Tips className={Styles.tips} direction="top" tips={t('tips_guide_edit_1')} />
          }
          <div className={Styles.subjectLabel}>主题分类</div>
          <div className={Styles.list}>
            {
              subjects.map(({ type_id, name }, index) => (
                <div
                  key={type_id}
                  className={cx(Styles.subject, {
                    [Styles.selected]: relatedContent.subject == type_id,
                  })}
                  onClick={() => handleSelectSubject(type_id)}
                >
                  { name }
                </div>
              ))
            }
          </div>
        </div>
        {/** 标题区 */}
        <div className={Styles.title}>
          <CommonInput
            required
            id="thread-title"
            label={t('thread_title')}
            maxLength={MAX_TITLE_LENGTH + 1}
            className={Styles.titleInput}
            placeholder={t('tips_thread_title')}
            errorMsg={t('error_thread_title_maximum')}
            value={relatedContent.title}
            onChange={(val) => setRelatedContent({ ...relatedContent, title: val })}
          />
        </div>
        {/** 主内容区 */}
        <div className={Styles.content}>
          {
            data.map((item, index) => {
              const { key, type, desc, duration, percent, isCompleted, url = '', imgsrc = '', error } = item;
              if (type == ContentTypeEnum.text) {
                return (
                  <div key={key} className={Styles.textareaWrapper}>
                    <CommonTextarea
                      id={key}
                      className={Styles.contentTextarea}
                      focused={currentFocusText === key}
                      autoResize
                      enterKeyHint="done"
                      placeholder={data.length > 1 ? '' : t('tips_thread_content')}
                      value={desc}
                      maxLength={MAX_DESC_LENGTH}
                      isMaxLength={calcTextLength(dataRef.current) >= MAX_DESC_LENGTH}
                      onChange={handleAddText}
                      onFocus={handleChangeFocusText}
                      onCreateNew={handleAddEmptyText}
                      onDelete={handleDeleteEmptyText}
                      onCombine={handleCombineText}
                    />
                  </div>
                );
              } else if (type == ContentTypeEnum.iframeLink || type == ContentTypeEnum.customLink) {
                let parsedUrl = url;
                if (url.startsWith('//player.bilibili.com')) {
                  parsedUrl = `https:${url}`;
                }

                return (
                  <LinkPreview
                    key={key}
                    id={key}
                    className={Styles.linkPreview}
                    link={parsedUrl}
                    title={desc}
                    isEdit
                    onRemove={(id) => handleOpenCheckModal(id, type)}
                  />
                )
              } else if (type == ContentTypeEnum.photo) {
                return (
                  <PhotoPreview
                    key={key}
                    id={key}
                    className={Styles.photoPreview}
                    url={url}
                    ratio={desc}
                    percent={percent}
                    completed={isCompleted}
                    error={error}
                    isEdit
                    onRemove={(id) => handleOpenCheckModal(id, type)}
                    onRetry={onRetryUpload}
                  />
                )
              } else if (type == ContentTypeEnum.video) {
                return (
                  <VideoPreview
                    key={key}
                    id={key}
                    className={Styles.videoPreview}
                    url={imgsrc}
                    percent={percent}
                    duration={duration}
                    error={error}
                    onRemove={(id) => handleOpenCheckModal(id, type)}
                    onRetry={onRetryUpload}
                  />
                );
              }
            })
          }
          {
            calcTextLength(dataRef.current) >= MAX_DESC_LENGTH && <p className={Styles.maxLengthTips}>{t("error_thread_content_maximum")}</p>
          }
        </div>
      </div>
      <div className={cx(Styles.gap, {
        [Styles.taller]: relatedContent.devices.length > 0
      })} />
      {/** 底部（吸底） */}
      <div className={Styles.bottom} style={{
        ['--safe-area-height' as string]: `${safeAreaHeight}px`
      }}>
        {
          showTips && <Tips className={Styles.tips} tips={t('tips_guide_edit_2')} />
        }
        <div className={cx(Styles.relatedDevices, {
          [Styles.taller]: relatedContent.devices.length > 0
        })}>
          {relatedContent.devices.length > 0 &&
            relatedContent.devices.map(({ key, name }: { key: string, name: string }) => (
              <React.Fragment key={key}>
                <div id={key} className={Styles.relatedDevice} onClick={() => {}}>
                  <div className={Styles.name}>{name}</div>
                  <div className={Styles.remove} onClick={() => handleDeleteDevice(key)} />
                </div>
              </React.Fragment>
            ))
          }
        </div>
        <div className={Styles.devices}>
          <div className={Styles.recommendDeviceLabel} onClick={handleOpenDeviceModal}>
            <span className={Styles.text}>关联设备</span>
            <div className={Styles.icon} />
          </div>
          <div className={Styles.list}>
            {
              recommendDevices.map(({ key, name, device }) => !isDeviceExist(key) &&  (
                <div
                  key={key}
                  className={Styles.device}
                  onClick={() => {
                    const isExist = isDeviceExist(key);
                    if (!isExist && relatedContent.devices.length >= MAX_RELATED_DEVICES) {
                      Toast.show({
                        content: t('error_device_maximum', { maximum: MAX_RELATED_DEVICES }),
                      });
                      return;
                    }

                    setRelatedContent({
                      ...relatedContent,
                      devices: relatedContent.devices.concat([{ key, name, device }]),
                    })}
                  }
                >
                  {name}
                  <span className={Styles.addIcon} />
                </div>
              ))
            }
          </div>
        </div>
        <div className={Styles.actions}>
          <div className={Styles.wrapper}>
            {
              actions.map(({ slug, label, callback, isUploadFile, accept, multiple }) => (
                <div className={Styles.actionWrapper} key={slug}>
                  <div className={Styles.action} onClick={callback}>
                    <div className={cx(Styles.actionIcon, { [Styles[slug]]: slug })} />
                    <div className={Styles.actionLabel}>{label}</div>
                  </div>
                  {
                    isUploadFile
                    &&
                    <input
                      accept={accept}
                      multiple={multiple}
                      type="file"
                      className={Styles.inputFile}
                      onClick={callback}
                      onChange={(e) => onChangeFile(e, slug)}
                    />
                  }
                </div>
              ))
            }
          </div>
        </div>
      </div>
      {
        linkModalVisbile && (
          <LinkModal
            textId={currentFocusText}
            onCancel={handleCancelLinkModal}
            onChange={handleAddLink}
          />
        )
      }
      {
        deviceModalVisible && (
          <DeviceModal
            deviceCategories={deviceCategories}
            selections={relatedContent.devices}
            onCancel={handleCancelDeviceModal}
            onChange={handleAddDevice}
          />
        )
      }
      {
        checkModal.visible && (
          <CommonPageModal
            title=''
            description={t('tips_thread_delete_content')}
            okText={t('btn_confirm')}
            exitText={t('btn_cancel')}
            isShowCloseIcon={false}
            onCancel={handleCloseCheckModal}
            onExit={handleCloseCheckModal}
            onConfirm={handleDeleteContent}
          />
        )
      }
      {
        IS_ANDROID_FLY && upgradeFlyAppModalVisible && (
          <CommonPageModal
            title=''
            description={t('upgrade_latest_version_tips', { version: '(1.13.5)'})}
            okText={t('upgrade_latest_version_tips_know_toast')}
            isShowCloseIcon={false}
            showExitBtn={false}
            onConfirm={() => setUpgradeFlyAppModalVisible(false)}
          />
        )
      }
    </div>
  );
};

export default ModuleEditor;
